import { useCreateJob } from '@/api/Jobs';
import { useTranslate } from '@/translations';
import {
  Battery12VChargeModel,
  BatteryCheckModel,
  CreateJobModel,
  EvChargeModel,
  JobTag,
  JobTypeEnum,
  SelectOption,
  TyrePressureModel,
  YesNoEnum,
  YesNoNAEnum,
} from '@/types';
import { Alert, Form, Input, InputNumber, Modal, Select, Spin } from 'antd';
import React, { FC, ReactNode, useEffect, useState } from 'react';
import {
  engineTypes,
  fluidTypes,
  fuelTypes,
  inputModes,
  jobSources,
  quantityTypes,
  recordActivityTypes,
  vehicleWashTypes,
} from '../constants';

interface JobPopupProps {
  jobTypes: SelectOption<JobTypeEnum>[];
  jobTags: JobTag[];
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

type JobPopupExtraData = Partial<
  EvChargeModel & Battery12VChargeModel & BatteryCheckModel & TyrePressureModel
> &
  Record<string, unknown>;

interface JobPopupFormValues
  extends Omit<CreateJobModel, 'jobType' | 'extraData'> {
  jobType?: JobTypeEnum;
  extraData?: JobPopupExtraData;
}

export const JobPopup: FC<JobPopupProps> = ({
  jobTypes,
  jobTags,
  open,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslate();
  const [form] = Form.useForm<JobPopupFormValues>();
  const type = Form.useWatch('jobType', form);

  const [errorMessage, setErrorMessage] = useState<string>();

  const { mutateAsync: createJobAsync, isPending: loading } = useCreateJob();

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({ vin: '' });
      setErrorMessage(undefined);
    }
  }, [form, open]);

  const EvChargeFields: FC = () => (
    <>
      <Form.Item label="Target">
        <div className="d-flex gap-1">
          <Form.Item name={['extraData', 'targetValue']} noStyle>
            <InputNumber precision={2} />
          </Form.Item>
          <Form.Item name={['extraData', 'inputMode']} noStyle>
            <Select
              placeholder="Please Choose"
              style={{ width: 100 }}
              options={inputModes}
            />
          </Form.Item>
        </div>
      </Form.Item>
    </>
  );
  const WarmUpFields: FC = () => (
    <Form.Item name={['extraData', 'targetMinutes']} label="Target Minutes">
      <InputNumber precision={0} step={1} />
    </Form.Item>
  );
  const AddFluidsFields: FC = () => (
    <>
      <Form.Item name={['extraData', 'fluidType']} label="Fluid Type">
        <Select placeholder="Please Choose" options={fluidTypes} />
      </Form.Item>
      <Form.Item label="Quantity">
        <div className="d-flex gap-1">
          <Form.Item name={['extraData', 'quantity']} noStyle>
            <InputNumber precision={0} step={1} />
          </Form.Item>
          <Form.Item name={['extraData', 'quantityType']} noStyle>
            <Select
              placeholder="Please Choose"
              style={{ width: 100 }}
              options={quantityTypes.map((o) => ({
                label: o,
                value: o,
              }))}
            />
          </Form.Item>
        </div>
      </Form.Item>
      <Form.Item name={['extraData', 'reason']} label="Reason">
        <Input.TextArea rows={3} />
      </Form.Item>
    </>
  );
  const AddFuelFields: FC = () => (
    <>
      <Form.Item name={['extraData', 'fuelType']} label="Fuel Type">
        <Select placeholder="Please Choose" options={fuelTypes} />
      </Form.Item>
      <Form.Item label="Quantity">
        <div className="d-flex gap-1">
          <Form.Item name={['extraData', 'quantity']} noStyle>
            <InputNumber precision={0} step={1} />
          </Form.Item>
          <Form.Item name={['extraData', 'quantityType']} noStyle>
            <Select
              placeholder="Please Choose"
              style={{ width: 100 }}
              options={quantityTypes.map((o) => ({
                label: o,
                value: o,
              }))}
            />
          </Form.Item>
        </div>
      </Form.Item>
      <Form.Item name={['extraData', 'comment']} label="Comment">
        <Input.TextArea rows={3} />
      </Form.Item>
    </>
  );
  const ActivityFields: FC = () => (
    <Form.Item name={['extraData', 'recordActivityType']} label="Activity Type">
      <Select placeholder="Please Choose" options={recordActivityTypes} />
    </Form.Item>
  );
  const WashFields: FC = () => (
    <Form.Item name={['extraData', 'vehicleWashType']} label="Wash Type">
      <Select placeholder="Please Choose" options={vehicleWashTypes} />
    </Form.Item>
  );
  const BatteryCheckFields: FC = () => (
    <>
      <Form.Item name={['extraData', 'engineType']} label="Engine Type">
        <Select placeholder="Please Choose" options={engineTypes} />
      </Form.Item>
      <Form.Item name={['extraData', 'transportMode']} label="Transport Mode">
        <Select
          placeholder="Please Choose"
          options={Object.values(YesNoEnum).map((value) => ({
            label: value,
            value,
          }))}
        />
      </Form.Item>
      <Form.Item
        name={['extraData', 'battery12VHealth']}
        label="Battery In Good Condition 12V"
      >
        <Select
          placeholder="Please Choose"
          options={Object.values(YesNoNAEnum).map((value) => ({
            label: value,
            value,
          }))}
        />
      </Form.Item>
      <Form.Item
        name={['extraData', 'battery12VChargeRequired']}
        label="Battery Require Charge 12V"
      >
        <Select
          placeholder="Please Choose"
          options={Object.values(YesNoNAEnum).map((value) => ({
            label: value,
            value,
          }))}
        />
      </Form.Item>
      <Form.Item
        name={['extraData', 'batteryHVHealth']}
        label="Battery Healthy HVB"
      >
        <Select
          placeholder="Please Choose"
          options={Object.values(YesNoNAEnum).map((value) => ({
            label: value,
            value,
          }))}
        />
      </Form.Item>
      <Form.Item
        name={['extraData', 'batteryHVChargeRequired']}
        label="Battery HV Charge Required"
      >
        <Select
          placeholder="Please Choose"
          options={Object.values(YesNoNAEnum).map((value) => ({
            label: value,
            value,
          }))}
        />
      </Form.Item>
    </>
  );
  const Battery12VChargeFields: FC = () => (
    <>
      <Form.Item label="Start Value">
        <div className="d-flex gap-1">
          <Form.Item name={['extraData', 'startValue']} noStyle>
            <InputNumber precision={2} />
          </Form.Item>
          <Form.Item name={['extraData', 'startUnit']} noStyle>
            <Select
              placeholder="Please Choose"
              style={{ width: 120 }}
              options={[
                { label: 'Voltage', value: 'Voltage' },
                { label: 'Percent', value: 'Percent' },
              ]}
            />
          </Form.Item>
        </div>
      </Form.Item>
      <Form.Item label="End Value">
        <div className="d-flex gap-1">
          <Form.Item name={['extraData', 'endValue']} noStyle>
            <InputNumber precision={2} />
          </Form.Item>
          <Form.Item name={['extraData', 'endUnit']} noStyle>
            <Select
              placeholder="Please Choose"
              style={{ width: 120 }}
              options={[
                { label: 'Voltage', value: 'Voltage' },
                { label: 'Percent', value: 'Percent' },
              ]}
            />
          </Form.Item>
        </div>
      </Form.Item>
    </>
  );
  const TyrePressureFields: FC = () => (
    <>
      <Form.Item
        name={['extraData', 'tyrePressureThreshold']}
        label="Tyre Pressure Threshold"
      >
        <InputNumber precision={2} suffix="Bar" className="w-50" />
      </Form.Item>
      <Form.Item
        name={['extraData', 'tyrePressureSet']}
        label="Tyre Pressure Set"
      >
        <InputNumber precision={2} suffix="Bar" className="w-50" />
      </Form.Item>
    </>
  );
  const JobTypeFields: Record<JobTypeEnum, () => ReactNode> = {
    [JobTypeEnum.EVCharging]: () => <EvChargeFields />,
    [JobTypeEnum.WarmUpEngine]: () => <WarmUpFields />,
    [JobTypeEnum.AddFluids]: () => <AddFluidsFields />,
    [JobTypeEnum.AddFuel]: () => <AddFuelFields />,
    [JobTypeEnum.RecordActivity]: () => <ActivityFields />,
    [JobTypeEnum.VehicleWash]: () => <WashFields />,
    [JobTypeEnum.BatteryCheck]: () => <BatteryCheckFields />,
    [JobTypeEnum.Inspection]: () => null,
    [JobTypeEnum.Refurbishment]: () => null,
    [JobTypeEnum.CompoundMovement]: () => null,
    [JobTypeEnum.Maintenance]: () => null,
    [JobTypeEnum.Battery12VCharge]: () => <Battery12VChargeFields />,
    [JobTypeEnum.TyrePressure]: () => <TyrePressureFields />,
  } as const;

  const handleSubmit = async (values: JobPopupFormValues) => {
    if (!type) return;

    const res = await createJobAsync(values as CreateJobModel);
    if (!res.success) {
      setErrorMessage(res.message.Message);
      return;
    }
    onSubmit();
    onClose();
  };

  return (
    <Modal
      title="Create Job"
      open={open}
      onCancel={onClose}
      okText={t('save')}
      cancelText={t('cancel')}
      onOk={() => form.submit()}
      confirmLoading={loading}
      width="50vw"
    >
      <Spin spinning={loading}>
        <Form
          id="job-form"
          form={form}
          onFinish={handleSubmit}
          size="large"
          labelCol={{ flex: `0 0 130px` }}
          labelWrap
          autoComplete="off"
        >
          <Form.Item<CreateJobModel>
            name="jobType"
            label="Job Type"
            rules={[{ required: true }]}
          >
            <Select
              placeholder="Choose Job Type"
              options={jobTypes.map((o) => ({
                label: o.text,
                value: o.value,
              }))}
              showSearch
            />
          </Form.Item>
          <Form.Item<CreateJobModel>
            name="vin"
            label="VIN"
            rules={[{ required: true }]}
          >
            <Input placeholder="Please Input" />
          </Form.Item>
          <Form.Item<CreateJobModel> name="jobSource" label="Job Source">
            <Select placeholder="Please Choose" options={jobSources} />
          </Form.Item>
          <Form.Item<CreateJobModel> name="jobTags" label="Job Tag">
            <Select
              mode="multiple"
              placeholder="Please Choose"
              options={jobTags.map((o) => ({ label: o.name, value: o.id }))}
            />
          </Form.Item>

          {type && JobTypeFields[type as JobTypeEnum]?.()}

          {errorMessage ? (
            <Alert message={errorMessage} type="error" className="mb-4" />
          ) : null}
        </Form>
      </Spin>
    </Modal>
  );
};
